import type { ProvinceDTO, ZoneDTO } from "../api/client";
import { RowActions } from "@/shared/ui/RowActions";
import { DataTable, type TanstackReactTable } from "@devhop/ui";
import { EditIcon } from "lucide-react";

function nameLo(row: ProvinceDTO): string {
  return (row.name as { lo?: string })?.lo ?? "—";
}

type ProvincesTableProps = {
  canManage: boolean;
  zones: ZoneDTO[];
  isLoading: boolean;
  data: ProvinceDTO[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onEdit: (row: ProvinceDTO) => void;
  onToggleEnabled: (id: string) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
};

function zoneLabel(zoneId: string | null, zones: ZoneDTO[]): string {
  if (!zoneId) return "—";
  const z = zones.find((x) => x.id === zoneId);
  return z ? z.code : zoneId;
}

export function ProvincesTable({
  canManage,
  zones,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onEdit,
  onToggleEnabled,
  onPaginationChange,
  onSortingChange,
}: ProvincesTableProps) {
  const columns: TanstackReactTable.ColumnDef<ProvinceDTO>[] = [
    {
      id: "nameLo",
      header: "ຊື່ (ລາວ)",
      cell: ({ row }) => nameLo(row.original),
      size: 120,
    },
    {
      id: "nameTh",
      header: "ຊື່ (ไทย)",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {(row.original.name as { th?: string })?.th ?? "—"}
        </span>
      ),
      size: 100,
    },
    {
      id: "nameEn",
      header: "ຊື່ (EN)",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {(row.original.name as { en?: string })?.en ?? "—"}
        </span>
      ),
      size: 100,
    },
    {
      id: "zoneId",
      header: "ໂຊນ",
      cell: ({ row }) => zoneLabel(row.original.zoneId, zones),
      size: 80,
    },
    {
      id: "enabled",
      header: "ສະຖານະ",
      cell: ({ row }) => (row.original.enabled ? "ເປີດ" : "ປິດ"),
      size: 80,
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const id = row.original.id;
        if (!canManage) return null;
        const actions = [
          {
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(row.original),
          },
          {
            label: row.original.enabled ? "ປິດ" : "ເປີດ",
            onClick: () => onToggleEnabled(id),
          },
        ];
        return <RowActions actions={actions} maxInline={2} />;
      },
      size: 100,
    },
  ];

  return (
    <div data-tourid="geo-provinces-table">
      <DataTable<ProvinceDTO, unknown>
        noDataMessage="ບໍ່ພົບແຂວງ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(s) => {
          if (s[0]?.id === "") return;
          onSortingChange(s[0]?.id as string, !!s[0]?.desc);
        }}
      />
    </div>
  );
}
